import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, Download, CheckCircle2, XCircle, Filter } from "lucide-react";
import api from "../lib/api";
import { statusClass, exportCSV } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PageHeader from "./PageHeader";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const APPROVER_ROLES = ["admin", "director", "finance", "manager"];

export default function ResourceManager({
  title, subtitle, icon, endpoint, columns, fields,
  searchKeys = ["name"], approvals = null, defaultValues = {},
}) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setRows(data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => { init[f.key] = f.default ?? (f.type === "switch" ? false : ""); });
    setForm({ ...init, ...defaultValues });
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({ ...row });
    setEditing(row);
    setOpen(true);
  };

  const save = async () => {
    const required = fields.filter((f) => f.required && !form[f.key]);
    if (required.length) {
      toast.error(`Please fill: ${required.map((r) => r.label).join(", ")}`);
      return;
    }
    try {
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, form);
        toast.success(`${title.replace(/s$/, "")} updated`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${title.replace(/s$/, "")} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`${endpoint}/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const doApprove = async (row, status) => {
    try {
      await api.post(`${approvals.module}/${row.id}/approve`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const filtered = rows.filter((r) =>
    !query || searchKeys.some((k) => String(r[k] || "").toLowerCase().includes(query.toLowerCase()))
  );

  const canApprove = approvals && APPROVER_ROLES.includes(user?.role);

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} icon={icon}>
        <Button variant="outline" size="sm" onClick={() => exportCSV(filtered, `${endpoint.replace("/", "")}.csv`)} data-testid="export-csv-btn">
          <Download className="h-4 w-4 mr-1.5" /> Export
        </Button>
        <Button size="sm" onClick={openCreate} className="bg-navy-900 hover:bg-navy-800" data-testid="add-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New
        </Button>
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…"
              className="pl-9 h-9 bg-slate-50 border-slate-200" data-testid="resource-search" />
          </div>
          <span className="text-xs text-slate-400 ml-auto flex items-center gap-1">
            <Filter className="h-3.5 w-3.5" /> {filtered.length} records
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                {columns.map((c) => (
                  <th key={c.key} className="text-left font-semibold text-[11px] uppercase tracking-wider text-slate-500 px-4 py-3 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-slate-400">No records found</td></tr>
              ) : filtered.map((row) => (
                <tr key={row.id} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors" data-testid={`row-${row.id}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 align-middle whitespace-nowrap">
                      {c.badge ? (
                        <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${statusClass(row[c.key])}`}>
                          {row[c.key] || "—"}
                        </span>
                      ) : c.render ? c.render(row) : (
                        <span className={c.primary ? "font-medium text-navy-900" : "text-slate-600"}>
                          {row[c.key] ?? "—"}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      {canApprove && (
                        <>
                          <button onClick={() => doApprove(row, approvals.nextStatus(row))} title="Approve"
                            className="p-1.5 rounded-md hover:bg-emerald-50 text-emerald-600" data-testid={`approve-${row.id}`}>
                            <CheckCircle2 className="h-4 w-4" />
                          </button>
                          <button onClick={() => doApprove(row, "Rejected")} title="Reject"
                            className="p-1.5 rounded-md hover:bg-red-50 text-red-500" data-testid={`reject-${row.id}`}>
                            <XCircle className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button onClick={() => openEdit(row)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" data-testid={`edit-${row.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setDeleteId(row.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500" data-testid={`delete-${row.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "New"} {title.replace(/s$/, "")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </label>
                {f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`field-${f.key}`} rows={3} />
                ) : f.type === "switch" ? (
                  <div className="flex items-center gap-2 h-10">
                    <Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} data-testid={`field-${f.key}`} />
                    <span className="text-sm text-slate-500">{form[f.key] ? "Yes" : "No"}</span>
                  </div>
                ) : (
                  <Input type={f.type || "text"} value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                    data-testid={`field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-navy-900 hover:bg-navy-800" data-testid="save-btn">
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
